import type { PersistedClient, Persister } from '@tanstack/react-query-persist-client';

const CONTENTFUL_CACHE_KEY = 'contentful-query-cache';

/** 4 hours in milliseconds */
const MAX_AGE_MS = 1000 * 60 * 60 * 4;

/** ISO 8601 date string regex pattern */
const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z?$/;

/**
 * JSON reviver function that converts ISO date strings back to Date objects.
 * This is needed because JSON.stringify converts Date objects to strings.
 */
function dateReviver(_key: string, value: unknown): unknown {
  if (typeof value === 'string' && ISO_DATE_REGEX.test(value)) {
    const date = new Date(value);
    // Only return Date if it's valid
    if (!Number.isNaN(date.getTime())) {
      return date;
    }
  }
  return value;
}

/**
 * Checks if a query key belongs to Contentful queries
 */
function isContentfulQueryKey(queryKey: readonly unknown[]): boolean {
  return queryKey[0] === 'contentful';
}

/**
 * Filters the persisted client to only include Contentful queries
 */
function filterContentfulQueries(client: PersistedClient): PersistedClient {
  return {
    ...client,
    clientState: {
      ...client.clientState,
      queries: client.clientState.queries.filter((query) =>
        isContentfulQueryKey(query.queryKey)
      ),
      mutations: [], // Don't persist mutations
    },
  };
}

/**
 * Creates a localStorage persister that only persists Contentful queries.
 * This reduces Contentful API calls by caching release notes for 4 hours,
 * even across browser refreshes.
 */
export function createContentfulPersister(): Persister {
  return {
    persistClient: async (client: PersistedClient) => {
      try {
        const filteredClient = filterContentfulQueries(client);
        
        // Only persist if there are Contentful queries to save
        if (filteredClient.clientState.queries.length > 0) {
          localStorage.setItem(
            CONTENTFUL_CACHE_KEY,
            JSON.stringify(filteredClient)
          );
        }
      } catch (error) {
        // localStorage might be unavailable or full
        console.warn('Failed to persist Contentful cache:', error);
      }
    },

    restoreClient: async () => {
      try {
        const cached = localStorage.getItem(CONTENTFUL_CACHE_KEY);
        if (!cached) {
          return undefined;
        }

        // Use dateReviver to convert ISO date strings back to Date objects
        const client = JSON.parse(cached, dateReviver) as PersistedClient;

        // Check if cache is still valid (within max age)
        const cacheAge = Date.now() - client.timestamp;
        if (cacheAge > MAX_AGE_MS) {
          localStorage.removeItem(CONTENTFUL_CACHE_KEY);
          return undefined;
        }

        return client;
      } catch (error) {
        // Handle corrupted cache or parse errors
        console.warn('Failed to restore Contentful cache:', error);
        localStorage.removeItem(CONTENTFUL_CACHE_KEY);
        return undefined;
      }
    },

    removeClient: async () => {
      try {
        localStorage.removeItem(CONTENTFUL_CACHE_KEY);
      } catch (error) {
        console.warn('Failed to remove Contentful cache:', error);
      }
    },
  };
}

/**
 * Clears the Contentful cache from localStorage.
 * Useful when you want to force a refresh of release notes.
 */
export function clearContentfulCache(): void {
  try {
    localStorage.removeItem(CONTENTFUL_CACHE_KEY);
  } catch (error) {
    console.warn('Failed to clear Contentful cache:', error);
  }
}
